import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const ChartPreview = ({ chartTemplate, chartType }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartTemplate || !chartRef.current) return;

    // 차트 인스턴스 생성 또는 재사용
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chartOptions = chartTemplate.chart_options?.options || chartTemplate.options;
    const vizType = (chartTemplate.viz_type || chartTemplate.chart_options?.viz_type)?.toLowerCase();
    const dataResult = chartTemplate.data_result;
    
    // chartType prop이 전달되면 사용 (progress_bar 구분용)
    const actualChartType = chartType || vizType;

    // 특수 차트 타입 처리
    if (vizType === 'battery_grid') {
      // 배터리 그리드는 HTML로 렌더링
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 20px;
        ">
          <div style="
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 15px;
            color: #333;
          ">
            ${chartTemplate.title || '배터리 현황'}
          </div>
          <div style="
            display: grid;
            grid-template-columns: repeat(4, 60px);
            gap: 10px;
          ">
            ${[1,2,3,4,5,6,7,8].map(i => `
              <div style="
                width: 60px;
                height: 80px;
                background: linear-gradient(to bottom, #4caf50 ${85 - i*10}%, #e0e0e0 ${85 - i*10}%);
                border: 2px solid #333;
                border-radius: 4px;
                display: flex;
                align-items: flex-end;
                justify-content: center;
                padding-bottom: 5px;
                font-size: 11px;
                font-weight: bold;
                color: #333;
              ">
                ${85 - i*10}%
              </div>
            `).join('')}
          </div>
          <div style="
            font-size: 12px;
            color: #666;
            margin-top: 10px;
          ">
            배터리 SOC 상태
          </div>
        </div>
      `;
      return;
    }
    
    if (vizType === 'date_picker') {
      // DatePicker 프리뷰
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 20px;
          background: #ffffff;
        ">
          <div style="
            font-size: 16px;
            font-weight: bold;
            color: #333;
            margin-bottom: 15px;
          ">
            ${chartTemplate.title || '조회 기간'}
          </div>
          <div style="
            display: flex;
            gap: 8px;
            margin-bottom: 15px;
            flex-wrap: wrap;
            justify-content: center;
          ">
            ${['오늘', '어제', '최근 7일', '최근 30일', '이번 달', '올해'].map(text => `
              <button style="
                padding: 6px 12px;
                border: 1px solid #e1e8ed;
                background-color: ${text === '최근 7일' ? '#e8f4fd' : '#fff'};
                border-radius: 20px;
                font-size: 13px;
                color: ${text === '최근 7일' ? '#4a90e2' : '#657786'};
                cursor: pointer;
              ">
                ${text}
              </button>
            `).join('')}
          </div>
          <div style="
            border: 1px solid #e1e8ed;
            border-radius: 4px;
            padding: 8px 12px;
            background: #fff;
            font-size: 14px;
            color: #333;
            min-width: 200px;
            text-align: center;
          ">
            📅 2025-01-01 ~ 2025-01-07
          </div>
          <div style="
            font-size: 12px;
            color: #666;
            margin-top: 10px;
          ">
            날짜 선택 시 변수를 발행합니다
          </div>
        </div>
      `;
      return;
    }
    
    if (vizType === 'navermap') {
      // 네이버 지도는 placeholder로 표시
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f0f0f0;
        ">
          <div style="font-size: 48px; margin-bottom: 10px;">🗺️</div>
          <div style="
            font-size: 16px;
            font-weight: bold;
            color: #333;
            margin-bottom: 5px;
          ">
            ${chartTemplate.title || '네이버 지도'}
          </div>
          <div style="
            font-size: 14px;
            color: #666;
          ">
            지도가 여기에 표시됩니다
          </div>
        </div>
      `;
      return;
    }
    
    // 프로그레스 바는 HTML로 렌더링
    if (vizType === 'progress_bar' || actualChartType === 'progress_bar') {
      const container = chartRef.current;
      const gaugeValue = chartTemplate.data_result?.series?.[0]?.values?.[0] || 91;
      const gaugeName = chartTemplate.data_result?.series?.[0]?.name || '듄소';
      const percentage = gaugeValue; // 이미 퍼센트 값으로 가정
      
      // 색상 결정
      let color = '#52c41a'; // 기본 녹색
      if (percentage <= 20) color = '#ff4d4f'; // 빨간색
      else if (percentage <= 40) color = '#faad14'; // 주황색
      else if (percentage <= 60) color = '#fadb14'; // 노란색
      else if (percentage <= 80) color = '#52c41a'; // 연두색
      
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 20px;
        ">
          <div style="
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
            color: #333;
          ">
            ${chartTemplate.title || '배터리 충전 상태'}
          </div>
          <div style="
            width: 100%;
            max-width: 300px;
          ">
            <div style="
              font-size: 14px;
              color: #666;
              margin-bottom: 8px;
            ">
              ${gaugeName}
            </div>
            <div style="
              position: relative;
              width: 100%;
              height: 30px;
              background-color: #f0f0f0;
              border-radius: 15px;
              overflow: hidden;
              box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
            ">
              <div style="
                width: ${percentage}%;
                height: 100%;
                background: linear-gradient(180deg, ${color} 0%, ${color}dd 100%);
                border-radius: 15px;
                transition: width 0.5s ease-in-out;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              "></div>
              <div style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 14px;
                font-weight: bold;
                color: ${percentage > 50 ? '#fff' : '#333'};
                text-shadow: ${percentage > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'};
              ">
                ${gaugeValue}%
              </div>
            </div>
            <div style="
              display: flex;
              justify-content: space-between;
              margin-top: 5px;
              font-size: 11px;
              color: #999;
            ">
              <span>0</span>
              <span>50</span>
              <span>100</span>
            </div>
          </div>
        </div>
      `;
      return;
    }
    
    if (vizType === 'webframe') {
      // 웹프레임은 placeholder로 표시
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
        ">
          <div style="font-size: 48px; margin-bottom: 10px;">🌐</div>
          <div style="
            font-size: 16px;
            font-weight: bold;
            color: #333;
            margin-bottom: 5px;
          ">
            ${chartTemplate.title || '외부 콘텐츠'}
          </div>
          <div style="
            font-size: 14px;
            color: #666;
          ">
            외부 웹페이지가 여기에 표시됩니다
          </div>
        </div>
      `;
      return;
    }
    
    if (vizType === 'text_label') {
      // 텍스트 라벨은 ECharts 대신 HTML로 렌더링
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
        ">
          <div style="
            font-size: ${chartOptions.fontSize || 48}px;
            font-weight: ${chartOptions.fontWeight || 'bold'};
            color: ${chartOptions.color || '#333'};
          ">
            ${chartOptions.value || '1,234 kWh'}
          </div>
          <div style="
            font-size: 14px;
            color: #666;
            margin-top: 10px;
          ">
            ${chartTemplate.title || ''}
          </div>
        </div>
      `;
      return;
    }

    if (vizType === 'badge') {
      // 배지는 HTML로 렌더링
      const container = chartRef.current;
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
        ">
          <div style="
            background-color: ${chartOptions.backgroundColor || '#e8f5e9'};
            color: ${chartOptions.color || '#27ae60'};
            font-size: ${chartOptions.fontSize || 24}px;
            padding: ${chartOptions.padding || 10}px 20px;
            border-radius: ${chartOptions.borderRadius || 8}px;
            font-weight: bold;
          ">
            ${chartOptions.value || '정상'}
          </div>
          <div style="
            font-size: 14px;
            color: #666;
            margin-top: 15px;
          ">
            ${chartTemplate.title || ''}
          </div>
        </div>
      `;
      return;
    }

    if (vizType === 'table') {
      // 테이블은 HTML로 렌더링
      const container = chartRef.current;
      
      // data_result 형식에서 columns와 rows 추출
      const columns = dataResult?.columns || [];
      const rows = dataResult?.rows || [];
      
      // 데이터가 없으면 빈 테이블 메시지 표시
      if (columns.length === 0 || rows.length === 0) {
        container.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            background: #f8f9fa;
          ">
            <div style="font-size: 32px; margin-bottom: 10px;">📊</div>
            <div style="
              font-size: 16px;
              font-weight: bold;
              color: #333;
              margin-bottom: 5px;
            ">
              ${chartTemplate.title || '테이블'}
            </div>
            <div style="
              font-size: 14px;
              color: #666;
            ">
              데이터가 테이블 형식으로 표시됩니다
            </div>
          </div>
        `;
        return;
      }
      
      let tableHTML = `
        <div style="
          padding: 10px;
          height: 100%;
          overflow: auto;
        ">
          <div style="
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #333;
            text-align: center;
          ">
            ${chartTemplate.title || ''}
          </div>
          <table style="
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          ">
            <thead>
              <tr style="
                background-color: ${chartOptions?.style?.headerBackground || '#f8f9fa'};
                color: ${chartOptions?.style?.headerColor || '#333'};
              ">
                ${columns.map(col => `
                  <th style="
                    padding: 10px;
                    border: 1px solid #dee2e6;
                    text-align: left;
                    font-weight: 600;
                    white-space: nowrap;
                  ">
                    ${col}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map((row, idx) => `
                <tr style="
                  background-color: ${idx % 2 === 0 ? 
                    (chartOptions?.style?.rowBackground || '#fff') : 
                    (chartOptions?.style?.rowAlternateBackground || '#fafafa')
                  };
                ">
                  ${columns.map((col, colIdx) => `
                    <td style="
                      padding: 8px 10px;
                      border: 1px solid #dee2e6;
                      color: #495057;
                    ">
                      ${Array.isArray(row) ? row[colIdx] : row[col] || ''}
                    </td>
                  `).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          ${rows.length > 0 ? `
            <div style="
              margin-top: 10px;
              padding: 5px;
              text-align: center;
              font-size: 12px;
              color: #6c757d;
            ">
              총 ${rows.length}개 행
            </div>
          ` : ''}
        </div>
      `;
      container.innerHTML = tableHTML;
      return;
    }

    // ECharts 차트 렌더링
    if (chartOptions || dataResult) {
      let finalOptions = {};
      
      // 백엔드 형식의 데이터가 있는 경우 ECharts 옵션으로 변환
      if (dataResult) {
        finalOptions = {
          title: {
            text: chartTemplate.title,
            left: 'center',
            top: 10,
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
              color: '#333'
            }
          },
          tooltip: {
            trigger: vizType === 'pie' ? 'item' : 'axis'
          },
          legend: {
            bottom: 10,
            data: dataResult.series?.map(s => s.name) || []
          },
          grid: {
            left: '10%',
            right: '10%',
            bottom: '15%',
            top: '10%',
            containLabel: true
          }
        };
        
        // 차트 타입별 옵션 설정
        if (vizType === 'bar' || vizType === 'line') {
          finalOptions.xAxis = {
            type: 'category',
            data: dataResult.categories || []
          };
          finalOptions.yAxis = {
            type: 'value'
          };
          finalOptions.series = dataResult.series?.map(s => ({
            name: s.name,
            type: vizType,
            data: s.values || s.data  // Handle both values and data
          })) || [];
        } else if (vizType === 'pie') {
          finalOptions.series = [{
            type: 'pie',
            radius: '50%',
            data: dataResult.categories?.map((cat, idx) => ({
              name: cat,
              value: (dataResult.series?.[0]?.values || dataResult.series?.[0]?.data)?.[idx] || 0
            })) || []
          }];
        } else if (vizType === 'scatter') {
          finalOptions.xAxis = { type: 'value' };
          finalOptions.yAxis = { type: 'value' };
          finalOptions.series = dataResult.series?.map(s => ({
            name: s.name,
            type: 'scatter',
            data: s.values || s.data
          })) || [];
        } else if (vizType === 'gauge') {
          // 게이지 차트 처리 (일반 게이지와 프로그레스 게이지 모두)
          const gaugeValue = (dataResult.series?.[0]?.values || dataResult.series?.[0]?.data)?.[0] || 0;
          const gaugeName = dataResult.series?.[0]?.name || '';
          
          // actualChartType이 progress_bar이거나 colorSteps가 정의되어 있으면 프로그레스 바로 처리
          const isProgressGauge = actualChartType === 'progress_bar' || chartOptions?.colorSteps;
          
          // 색상 단계 설정
          const colorSteps = isProgressGauge ? 
            chartOptions?.colorSteps || [
              [0.2, '#ff4d4f'],  // 0-20%: 빨간색
              [0.4, '#faad14'],  // 20-40%: 주황색
              [0.6, '#fadb14'],  // 40-60%: 노란색
              [0.8, '#52c41a'],  // 60-80%: 연두색
              [1, '#52c41a']     // 80-100%: 녹색
            ] : [
              [0.3, '#91c7ae'],
              [0.7, '#63869e'],
              [1, '#c23531']
            ];
          
          finalOptions.series = [{
            type: 'gauge',
            radius: '75%',
            center: ['50%', '60%'],
            min: chartOptions?.min || 0,
            max: chartOptions?.max || 100,
            splitNumber: 10,
            axisLine: {
              lineStyle: {
                width: isProgressGauge ? 20 : 15,
                color: colorSteps
              }
            },
            pointer: {
              length: '70%',
              width: 8,
              offsetCenter: [0, '-5%'],
              itemStyle: {
                color: 'auto'
              }
            },
            axisTick: {
              length: 8,
              lineStyle: {
                color: 'auto',
                width: 1
              }
            },
            splitLine: {
              length: 15,
              lineStyle: {
                color: 'auto',
                width: 2
              }
            },
            axisLabel: {
              color: 'auto',
              fontSize: 12,
              distance: 20
            },
            detail: {
              valueAnimation: true,
              fontSize: 32,
              fontWeight: 'bold',
              color: 'auto',
              offsetCenter: [0, '35%'],
              formatter: '{value}' + (chartOptions?.unit || '%')
            },
            title: {
              show: !!gaugeName,
              fontSize: 16,
              color: '#666',
              offsetCenter: [0, '55%']
            },
            data: [{
              value: gaugeValue,
              name: gaugeName
            }]
          }];
        } else if (vizType === 'radar') {
          finalOptions.radar = {
            indicator: dataResult.categories?.map(cat => ({
              name: cat,
              max: 100
            })) || []
          };
          finalOptions.series = [{
            type: 'radar',
            data: dataResult.series?.map(s => ({
              name: s.name,
              value: s.values || s.data  // Handle both for compatibility
            })) || []
          }];
        } else if (vizType === 'heatmap') {
          // 히트맵은 특별한 데이터 형식이 필요
          const heatmapData = [];
          dataResult.categories?.forEach((cat, x) => {
            dataResult.series?.forEach((series, y) => {
              heatmapData.push([x, y, (series.values || series.data)?.[x] || 0]);
            });
          });
          finalOptions.xAxis = {
            type: 'category',
            data: dataResult.categories || []
          };
          finalOptions.yAxis = {
            type: 'category',
            data: dataResult.series?.map(s => s.name) || []
          };
          finalOptions.visualMap = {
            min: 0,
            max: 100,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '0%'
          };
          finalOptions.series = [{
            type: 'heatmap',
            data: heatmapData,
            label: {
              show: true
            }
          }];
        }
      } else if (chartOptions) {
        // 기존 ECharts 옵션 형식인 경우
        finalOptions = {
          ...chartOptions,
          title: {
            text: chartTemplate.title,
            left: 'center',
            top: 10,
            textStyle: {
              fontSize: chartOptions.style?.title?.fontSize || 16,
              fontWeight: chartOptions.style?.title?.fontWeight || 'bold',
              color: chartOptions.style?.title?.color || '#333'
            }
          },
          grid: {
            left: '10%',
            right: '10%',
            bottom: '10%',
            top: '15%',
            containLabel: true
          }
        };
      }

      // 차트 타입별 특수 처리
      if (vizType === 'donut' && finalOptions.series?.[0]) {
        // 도넛 차트의 경우 viz_type을 처리
        finalOptions.series[0].type = 'pie';
      }

      try {
        chartInstance.current.setOption(finalOptions, true);
      } catch (error) {
        console.error('차트 렌더링 오류:', error);
      }
    }

    // 리사이즈 핸들러
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      // 컴포넌트 언마운트 시 차트 인스턴스 정리
      if (chartInstance.current) {
        try {
          // 툴팁 명시적으로 숨기기
          chartInstance.current.dispatchAction({
            type: 'hideTip'
          });
          // 차트 인스턴스 정리
          chartInstance.current.dispose();
        } catch (error) {
          console.warn('차트 정리 중 오류:', error);
        }
        chartInstance.current = null;
      }
    };
  }, [chartTemplate, chartType]);

  return (
    <div 
      ref={chartRef}
      style={{ 
        width: '100%', 
        height: '400px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    />
  );
};

export default ChartPreview;